import { characterConst } from 'utils/constant'
import {
  SET_AUTHENTICATED,
  SET_UNAUTHENTICATED,
  SET_PROFILE,
  LOADING_USER,
  ACCOUNT_SET_LEVEL_DONE,
  ACCOUNT_SET_CHARACTER_DONE,
  ACCOUNT_SET_LANGUAGE_DONE,
  ACCOUNT_SET_FIRST_NAME_DONE,
  ACCOUNT_SET_EMAIL_DONE,
  ACCOUNT_SET_PASSWORD_DONE,
  ACCOUNT_UPDATE_ERRORS,
  ACCOUNT_SET_HACK_CHINESE,
  ACCOUNT_REMOVE_HACK_CHINESE,
  ACCOUNT_SET_SKRITTER,
  ACCOUNT_REMOVE_SKRITTER,
  SET_LEVEL,
  SET_AUTH_LOADER,
} from '../types'

interface TrialEligibilityState {
  isEligible: boolean
  reason: 'first_time' | 'reactivation' | 'ineligible'
}

/** Back-to-School 2026 campaign eligibility, mirrored from the profile endpoint. */
interface BackToSchool2026State {
  isEligible: boolean
  endsAt?: string
}

interface IUserState {
  level: string
  authenticated: boolean
  credentials: {
    memberId: string
    email: string
    metaData: {
      firstName: string
    }
    customFields: {
      level: string
      'first-name': string
      user_exist: boolean
      character: string
      /** Interface and meanings language (ISO code). null until the learner has one. */
      native_language: string | null
      /** How native_language was set: 'detected' from the browser, or 'chosen' by the learner. */
      native_language_source?: 'detected' | 'chosen' | null
      /** Also show the English meaning under the learner-language one in the word panel. */
      show_english_meanings: boolean
    }
    level: string
    /** The account's `createdAt` from the profile or login response (ISO string), when known. */
    createdAt?: string | null
  }
  membership: {
    id: string
    name: string
    amount: number
    oneTime: boolean
    plan: string
  }
  stripe: any
  loading: boolean
  accountError: string
  user_auth_token: string
  authToken: string
  refreshToken: string
  paymentProfile: any
  lastLogin: Date
  skritter_token: string
  skritter: any
  reminder: string | null
  authLoader: boolean
  trialEligibility: TrialEligibilityState | null
  backToSchool2026: BackToSchool2026State | null
  subscriptionStatus: string | null
}

const initialState: IUserState = {
  authLoader: false,
  authenticated: false,
  level: '',
  credentials: {
    memberId: '',
    email: '',
    customFields: {
      level: '',
      'first-name': '',
      user_exist: false,
      character: characterConst.simplified,
      native_language: null,
      native_language_source: null,
      show_english_meanings: false,
    },
    metaData: {
      firstName: '',
    },
    level: '',
  },
  membership: {
    id: '',
    name: '',
    amount: 0,
    oneTime: false,
    plan: '',
  },
  stripe: null,
  loading: false,
  accountError: '',
  authToken: '',
  refreshToken: '',
  paymentProfile: null,
  lastLogin: new Date(),
  user_auth_token: '',
  skritter_token: '',
  skritter: '',
  reminder: null,
  trialEligibility: null,
  backToSchool2026: null,
  subscriptionStatus: null,
}
export default function userReducer(state = initialState, action: any) {
  switch (action.type) {
    case SET_LEVEL:
      return {
        ...state,
        level: action.payload,
      }
    case SET_AUTHENTICATED:
      return {
        ...state,
        authenticated: true,
        credentials: {
          ...state.credentials,
          ...action.payload.credentials,
        },
        membership: action.payload.membership,
        stripe: action.payload.stripe,
        authToken: action.payload.authToken,
        paymentProfile: action.payload.paymentProfile,
        refreshToken: action.payload.refreshToken,
        lastLogin: action.payload.lastLogin,
        reminder: action.payload.reminder,
        trialEligibility: action.payload.trialEligibility ?? null,
        backToSchool2026: action.payload.backToSchool2026 ?? null,
        subscriptionStatus: action.payload.subscriptionStatus ?? null,
      }

    case SET_UNAUTHENTICATED:
      return {
        ...initialState,
        authenticated: false,
      }

    case LOADING_USER:
      return {
        ...state,
        loading: true,
      }
    case SET_PROFILE:
      return {
        ...state,
        credentials: {
          ...state.credentials,
          ...action.payload.credentials,
        },
        membership: action.payload.membership,
        stripe: action.payload.stripe,
        paymentProfile: action.payload.paymentProfile,
        trialEligibility: action.payload.trialEligibility ?? state.trialEligibility,
        backToSchool2026: action.payload.backToSchool2026 ?? state.backToSchool2026,
        subscriptionStatus: action.payload.subscriptionStatus ?? state.subscriptionStatus,
      }

    case ACCOUNT_SET_FIRST_NAME_DONE:
      return {
        ...state,
        credentials: {
          ...state.credentials,
          customFields: {
            ...state.credentials.customFields,
            'first-name': action.payload,
          },
        },
      }
    case ACCOUNT_SET_EMAIL_DONE:
      return {
        ...state,
        credentials: {
          ...state.credentials,
          email: action.payload,
        },
      }
    case ACCOUNT_SET_LEVEL_DONE:
      return {
        ...state,
        credentials: {
          ...state.credentials,
          customFields: {
            ...state.credentials.customFields,
            level: action.payload,
          },
        },
      }
    case ACCOUNT_SET_CHARACTER_DONE:
      return {
        ...state,
        credentials: {
          ...state.credentials,
          customFields: {
            ...state.credentials.customFields,
            character: action.payload,
          },
        },
      }
    case ACCOUNT_SET_LANGUAGE_DONE:
      return {
        ...state,
        credentials: {
          ...state.credentials,
          customFields: {
            ...state.credentials.customFields,
            ...action.payload,
          },
        },
      }
    case ACCOUNT_SET_PASSWORD_DONE:
      return {
        ...state,
      }
    case ACCOUNT_UPDATE_ERRORS:
      return {
        ...state,
        accountError: action.payload,
      }
    case ACCOUNT_SET_HACK_CHINESE:
      return {
        ...state,
        user_auth_token: action.payload.token,
      }
    case ACCOUNT_REMOVE_HACK_CHINESE:
      return {
        ...state,
        user_auth_token: '',
      }
    case ACCOUNT_SET_SKRITTER:
      return {
        ...state,
        skritter_token: action.payload.token,
        skritter: action.payload.skritter,
      }
    case ACCOUNT_REMOVE_SKRITTER:
      return {
        ...state,
        skritter_token: '',
        skritter: '',
      }
    case SET_AUTH_LOADER:
      return {
        ...state,
        authLoader: action.payload,
      }
    default:
      return state
  }
}
